import { X, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface InviteErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: {
    code?: string;  // New structured error code
    message?: string;  // New structured message
    error?: string;  // Legacy error message
    errorType?: string;  // Legacy error type
    details?: any;
    technicalDetails?: {
      message: string;
      details?: string | null;
      stack?: string | null;
    };
    actionableSteps?: string[];
    timestamp?: string;
  };
  email: string;
}

export function InviteErrorModal({
  isOpen,
  onClose,
  error,
  email
}: InviteErrorModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  // Support both new structured errors and legacy errors
  const errorMessage = error.message || error.error || 'An error occurred';
  const errorCode = error.code || error.errorType;
  const steps = error.actionableSteps || [];

  const getIconAndColor = (errorCode?: string) => {
    // Info-level errors (user already exists, etc.)
    if (['USER_EXISTS', 'USER_ACTIVE', 'INFLUENCER_HAS_USER'].includes(errorCode || '')) {
      return {
        icon: Info,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-600',
        borderColor: 'border-blue-200',
        sectionBg: 'bg-blue-50'
      };
    }

    // Warning-level errors (cleanup needed, validation issues)
    if (['ORPHANED_USER', 'ORPHANED_USER_CLEANUP_FAILED', 'DB_ERROR', 'VALIDATION_ERROR', 'INVALID_JSON', 'INFLUENCER_ID_REQUIRED', 'INFLUENCER_NOT_FOUND', 'AUTH_DATABASE_ERROR'].includes(errorCode || '')) {
      return {
        icon: AlertTriangle,
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        sectionBg: 'bg-amber-50'
      };
    }

    // Error-level (critical errors)
    return {
      icon: AlertCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-600',
      borderColor: 'border-red-200',
      sectionBg: 'bg-red-50'
    };
  };

  const { icon: Icon, bgColor, textColor, borderColor, sectionBg } = getIconAndColor(errorCode);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 ${bgColor} rounded-full`}>
                <Icon className={`w-6 h-6 ${textColor}`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('admin.invitationFailed')}
                </h2>
                <p className="text-gray-600 mt-1">
                  {email}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className={`mb-6 p-4 ${sectionBg} border ${borderColor} rounded-lg`}>
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 ${textColor} mt-0.5`} />
              <div className="flex-1">
                <p className="font-semibold text-gray-900 mb-2">
                  {errorMessage}
                </p>

                {errorCode && (
                  <p className="text-xs font-mono text-gray-500 mb-3">
                    Error Code: {errorCode}
                  </p>
                )}

                {steps.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium text-gray-900 mb-2">
                      {t('admin.howToFix')}:
                    </p>
                    <ol className="space-y-2 text-sm text-gray-700">
                      {steps.map((step, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="font-bold text-gray-900 mt-0.5">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {(error.technicalDetails || error.details) && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                      {t('admin.technicalDetails')}
                    </summary>
                    <div className="mt-2 p-3 bg-white rounded border border-gray-300 max-h-60 overflow-auto">
                      {error.technicalDetails?.message && (
                        <p className="text-xs font-mono text-gray-700 mb-2">
                          <strong>Message:</strong> {error.technicalDetails.message}
                        </p>
                      )}
                      {error.technicalDetails?.details && (
                        <p className="text-xs font-mono text-gray-700 mb-2">
                          <strong>Details:</strong> {typeof error.technicalDetails.details === 'string'
                            ? error.technicalDetails.details
                            : JSON.stringify(error.technicalDetails.details, null, 2)}
                        </p>
                      )}
                      {error.details && (
                        <div className="text-xs font-mono text-gray-700 mb-2">
                          <strong>Details:</strong>
                          <pre className="mt-1 whitespace-pre-wrap">
                            {typeof error.details === 'string'
                              ? error.details
                              : JSON.stringify(error.details, null, 2)}
                          </pre>
                        </div>
                      )}
                      {errorCode && (
                        <p className="text-xs font-mono text-gray-700 mb-2">
                          <strong>Error Code:</strong> {errorCode}
                        </p>
                      )}
                      {error.timestamp && (
                        <p className="text-xs font-mono text-gray-700">
                          <strong>Timestamp:</strong> {new Date(error.timestamp).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">
              {t('admin.alternativeSolutions')}
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {['USER_EXISTS', 'USER_ACTIVE', 'INFLUENCER_HAS_USER'].includes(errorCode || '') ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>{t('admin.editExistingUser')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>{t('admin.sendPasswordReset')}</span>
                  </li>
                </>
              ) : errorCode === 'VALIDATION_ERROR' ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">•</span>
                    <span>Check the request data matches the required format</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">•</span>
                    <span>Review technical details for specific validation errors</span>
                  </li>
                </>
              ) : errorCode === 'FORBIDDEN' ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold mt-0.5">•</span>
                    <span>Contact an administrator to request user management permissions</span>
                  </li>
                </>
              ) : (['INFLUENCER_NOT_FOUND', 'INFLUENCER_ID_REQUIRED'].includes(errorCode || '')) ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">•</span>
                    <span>Verify the influencer profile exists in the Influencers tab</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">•</span>
                    <span>Create the influencer profile first before sending invitation</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-600 font-bold mt-0.5">•</span>
                    <span>{t('admin.checkSupabaseDashboard')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-600 font-bold mt-0.5">•</span>
                    <span>{t('admin.contactAdministrator')}</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
